import Norecords from "../../components/customHelpers/Norecords";
import Loading from "../../components/customHelpers/Loading";
import Layout from "../../components/layout/Layout";
import {
  Table,
  TableBody,
  TableRow,
  TableCell,
  TableHead,
  TableContainer,
  Typography,
  MenuItem,
  InputAdornment,
  useMediaQuery,
} from "@mui/material";
import { Grid, Box, Paper, Autocomplete, TextField } from "@mui/material";
import { useEffect, useState, useRef } from "react";
import Notify from "../../components/customHelpers/Notify";
import Notification from "../../components/customHelpers/Notification";
import Admin from "../../components/auth/Admin";
import Order from "../../actions/order";
import Customer from "../../actions/customer";
import Controls from "../../components/controls/Controls";
import { FiEye } from "react-icons/fi";
import { useHistory } from "react-router-dom";
import { useTheme } from "@mui/material/styles";
import { makeStyles } from "@mui/styles";
import ClearIcon from "@mui/icons-material/Clear";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import {
  calculateTotalSumOrderProducts,
  formatNumber,
  getTimestamp,
  paymentLists,
} from "../../utils/utils";
import PrintIcon from "@mui/icons-material/Print";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { DemoContainer } from "@mui/x-date-pickers/internals/demo";
import dayjs from "dayjs";
import html2pdf from "html2pdf.js";

const noPrintStyle = {
  display: "none",
};

const useStyles = makeStyles((theme) => ({
  table: {
  //  marginTop: theme.spacing(1),
    width: "100%",
    "& thead th": {
      fontWeight: "600",
      userSelect: "text",
      pointerEvents: "auto",
     // padding: theme.spacing(1),
      fontSize: "0.9rem",
      lineHeight: "1.2rem",
    },
    "& tbody td": {
      fontWeight: "300",
      padding: "2px",
      userSelect: "text",
      pointerEvents: "auto",
    },
    "& tbody tr:hover": {
      backgroundColor: "#fffbf2",
      cursor: "pointer",
    },
  },
  scrollButton: {
    position: "fixed !important",
    right: "1rem !important",
    bottom: "2rem !important",
    boxShadow: "rgba(149, 157, 165, 0.2) 0px 8px 24px",
    padding: "10px",
    borderRadius: "10px",
    background: "rgb(20, 61, 89)",
    color: "white",
    outline: "none",
    border: "none",
    cursor: "pointer",
    zIndex: 100,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },
}));

const headCells = [
  { id: "id", label: "Id" },
  { id: "assignTo", label: "Assigned To" },
  { id: "quantity", label: "Item Count" },
  { id: "totalPrice", label: "Total Price" },
  { id: "paid", label: "Paid" },
  { id: "unpaid", label: "Balance" },
  { id: "createdAt", label: "Date" },
  { id: "status", label: "Status" },
  { id: "actions", label: "Actions", disableSorting: true },
];

const TblHead = ({ headCells, isPrinting }) => {
  const theme = useTheme(); // Access the Material-UI theme
  return (
    <TableHead>
      <TableRow>
        {headCells.map((cell) => {
          if (cell.id === "actions" && isPrinting) return null;
          return (
            <TableCell
              key={cell.id}
              style={{
                fontWeight: 600,
                color: theme.palette.secondary.main,
                backgroundColor: theme.palette.primary.main,
                userSelect: "text",
                pointerEvents: "auto",
                padding: theme.spacing(1),
                fontSize: "0.9rem",
                lineHeight: "1.2rem",
              }}
            >
              {cell.label}
            </TableCell>
          );
        })}
      </TableRow>
    </TableHead>
  );
};

const PaymentReport = () => {
  const classes = useStyles();
  const history = useHistory();
  const { NotifyMessage, notify, setNotify } = Notify();
  const { viewAllOrders } = Order();
  const { viewAllCustomers } = Customer();
  const [isPrinting, setIsPrinting] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [orders, setOrders] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [startDate, setStartDate] = useState(
    dayjs().subtract(1, "month").startOf("day")
  );
  const [endDate, setEndDate] = useState(dayjs().endOf("day"));
  const [scrollToTopActive, setScrollToTopActive] = useState(true);
  const topRef = useRef(null);
  const bottomRef = useRef(null);

  useEffect(() => {
    getAllOrders();
    getAllCustomers();
    const handleScroll = () => {
      const isAtTop = window.scrollY === 0;
      const isAtBottom =
        window.innerHeight + window.scrollY >= document.body.offsetHeight;
      if (isAtTop) {
        setScrollToTopActive(false);
      } else if (isAtBottom) {
        setScrollToTopActive(true);
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleScrollClick = () => {
    if (scrollToTopActive) {
      scrollToTop();
    } else {
      scrollToBottom();
    }
    setScrollToTopActive(!scrollToTopActive);
  };

  const scrollToTop = () => {
    topRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const scrollToBottom = () => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const getIconRotation = () => {
    return scrollToTopActive ? "-90deg" : "90deg";
  };

  const getButtonLabel = () => {
    return scrollToTopActive ? "Back to Top" : "Back to Bottom";
  };

  const getAllCustomers = () => {
    viewAllCustomers().then((data) => {
      if (data.err) {
        NotifyMessage({ message: data.err, type: "error" });
      } else if (data.customers) {
        setCustomers(data.customers);
      }
    });
  };

  const getAllOrders = () => {
    viewAllOrders().then((data) => {
      if (data.err) {
        NotifyMessage({ message: data.err, type: "error" });
      } else if (data.orders) {
        const modifiedOrders = data.orders
          .filter((order) => order.status !== "Pending")
          .map((order) => ({
            ...order,
            customer: order?.customer?.fullName,
          }));
        setOrders(modifiedOrders);
        setLoading(false);
      }
    });
  };

  const groupOrdersByCustomer = (ordersArray) => {
    const groupedOrders = {};
    ordersArray.forEach((order) => {
      const customerId = order.customer;
      if (!groupedOrders[customerId]) {
        groupedOrders[customerId] = [];
      }
      groupedOrders[customerId].push(order);
    });
    return groupedOrders;
  };

  const calculateTotalPrice = (orders) => {
    return orders
      .reduce((total, order) => {
        return total + parseFloat(order.totalPrice || 0);
      }, 0)
      .toFixed(2);
  };

  const calculateTotalPaid = (orders) => {
    return orders
      .reduce((total, order) => {
        return total + parseFloat(order.paid || 0);
      }, 0)
      .toFixed(2);
  };

  const handlePrint = () => {
    setIsPrinting(true);

    setTimeout(() => {
      const input = document.getElementById("pdfContent");

      if (!input) return;
      const originalStyle = input.getAttribute("style");
      input.setAttribute("style", "font-size: 2px;");

      const opt = {
        filename: `Payment_Report_${getTimestamp()}.pdf`,
        image: { type: "jpeg", quality: 0.98 },
        html2canvas: { scale: 2, scrollY: -window.scrollY },
        jsPDF: { unit: "in", format: "a4", orientation: "portrait" },
        pagebreak: { mode: ["avoid-all", "css", "legacy"] },
      };

      html2pdf()
        .set(opt)
        .from(input)
        .save()
        .finally(() => {
          if (originalStyle !== null) {
            input.setAttribute("style", originalStyle);
          } else {
            input.removeAttribute("style");
          }
          setIsPrinting(false);
        });
    }, 0);
  };

  const filteredOrders = orders.filter((order) => {
    const orderCustomerFullName = order.customer;
    const orderDate = dayjs(order.createdAt);

    if (
      selectedCustomer &&
      orderCustomerFullName !== selectedCustomer.fullName
    ) {
      return false;
    }

    if (startDate) {
      const start = startDate.startOf("day");
      if (orderDate.isBefore(start)) {
        return false;
      }
    }

    if (endDate) {
      const end = endDate.endOf("day");
      if (orderDate.isAfter(end)) {
        return false;
      }
    }

    if (selectedPayment) {
      const paid = Number(order.paid);
      const totalPrice = parseFloat(calculateTotalPrice(order.orderProducts));

      if (selectedPayment === "Paid") {
        return paid > 0 && totalPrice - paid === 0;
      } else if (selectedPayment === "Balance") {
        return totalPrice - paid > 0 || totalPrice - paid < 0;
      }
    }

    return true;
  });

  const groupedOrders = groupOrdersByCustomer(filteredOrders);

  const calculateGrandTotalPrice = (ordersArray) => {
    return ordersArray
      .reduce((total, order) => {
        return (
          total + parseFloat(calculateTotalPrice(order.orderProducts) || 0)
        );
      }, 0)
      .toFixed(2);
  };

  const calculateGrandTotalPaid = (ordersArray) => {
    return ordersArray
      .reduce((total, order) => {
        return total + parseFloat(order.paid || 0);
      }, 0)
      .toFixed(2);
  };
  const isMedium = useMediaQuery("(max-width:1100px)");
  const ismedium = useMediaQuery("(min-width:700px) and (max-width:1100px)");

  return (
    <Layout>
      <Admin>
        <div className="card">
          <Typography variant="h6" className="page-header" ref={topRef}>
            Payment Report
          </Typography>
          <Typography variant="body2" color="textSecondary">
            The payment report shows a one-month report by default.
          </Typography>
          <Box
            display="flex"
            flexDirection={{ xs: "column", sm: "row" }}
            gap={2}
            m={1}
          >
            <Autocomplete
              options={customers}
              sx={{ width: { xs: "100%", sm: "20%" }, marginTop: "10px" }}
              value={selectedCustomer}
              disableClearable
              getOptionLabel={(option) => option.fullName}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select Customer"
                  variant="outlined"
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <InputAdornment position="end">
                        {selectedCustomer && (
                          <ClearIcon
                            onClick={() => setSelectedCustomer(null)}
                            style={{ cursor: "pointer" }}
                          />
                        )}
                        {params.InputProps.endAdornment}
                      </InputAdornment>
                    ),
                  }}
                />
              )}
              onChange={(event, value) => {
                setSelectedCustomer(value);
              }}
            />
            <Autocomplete
              options={paymentLists()}
              sx={{ width: { xs: "100%", sm: "20%" }, marginTop: "10px" }}
              value={selectedPayment}
              disableClearable
              getOptionLabel={(option) => option}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Filter By Payment"
                  variant="outlined"
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <InputAdornment position="end">
                        {selectedPayment && (
                          <ClearIcon
                            onClick={() => setSelectedPayment(null)}
                            style={{ cursor: "pointer" }}
                          />
                        )}
                        {params.InputProps.endAdornment}
                      </InputAdornment>
                    ),
                    sx: { height: 45 },
                  }}
                />
              )}
              onChange={(event, value) => setSelectedPayment(value)}
            />

            <Box sx={{ width: { xs: "100%", sm: ismedium ? "30%" : "20%" } }}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DemoContainer components={["DatePicker"]}>
                  <DatePicker
                    label="Start Date"
                    value={startDate}
                    onChange={setStartDate}
                    sx={{
                      width: "100%",
                      "& .MuiInputBase-root": { height: 46 },
                    }}
                    format="MM/DD/YYYY"
                    slotProps={{
                      field: {
                        clearable: true,
                        onClear: () => setStartDate(null),
                      },
                    }}
                  />
                </DemoContainer>
              </LocalizationProvider>
            </Box>
            <Box sx={{ width: { xs: "100%", sm: ismedium ? "30%" : "20%" } }}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DemoContainer components={["DatePicker"]}>
                  <DatePicker
                    label="End Date"
                    value={endDate}
                    onChange={setEndDate}
                    sx={{
                      "& .MuiInputBase-root": { height: 46 },
                    }}
                    slotProps={{
                      field: {
                        clearable: true,
                        onClear: () => setEndDate(null),
                      },
                    }}
                  />
                </DemoContainer>
              </LocalizationProvider>
            </Box>
            {!isMedium && (
              <Controls.Button
                text="Print"
                variant="outlined"
                startIcon={<PrintIcon />}
                className="newButton"
                onClick={handlePrint}
              />
            )}
          </Box>
          {isMedium && (
            <Controls.Button
              text="Print"
              variant="outlined"
              startIcon={<PrintIcon />}
              className="newButton"
              onClick={handlePrint}
            />
          )}
          <div id="pdfContent">
            <Grid container>
              <Grid item xs={12} className="card__body">
                {loading ? (
                  <Loading />
                ) : Object.keys(groupedOrders).length === 0 ? (
                  <Norecords />
                ) : (
                  Object.keys(groupedOrders).map((customerId) => (
                    <div key={customerId}>
                      <Typography
                        variant="body2"
                        fontWeight="bold"
                        whiteSpace="nowrap"
                      >
                        <span style={{ textTransform: "capitalize" }}>
                          {groupedOrders[customerId][0].customer}
                        </span>
                      </Typography>
                      <Paper
                        style={{ overflowX: "auto", marginBottom: "20px" }}
                      >
                        <TableContainer>
                          <Table className={classes.table}>
                            <TblHead
                              headCells={headCells}
                              isPrinting={isPrinting}
                            />
                            <TableBody>
                              {groupedOrders[customerId].map((item) => (
                                <TableRow key={item.id}>
                                  <TableCell>
                                    {item.orderId
                                      ? item.orderId
                                      : `${item.id.slice(0, 7)}--`}
                                  </TableCell>
                                  <TableCell>{item.staff?.fullName}</TableCell>
                                  <TableCell>
                                    {item?.orderProducts?.length}
                                  </TableCell>
                                  <TableCell>
                                    {formatNumber(
                                      calculateTotalPrice(item.orderProducts) ||
                                        0
                                    )}
                                  </TableCell>
                                  <TableCell>
                                    {formatNumber(item.paid)}
                                  </TableCell>
                                  <TableCell>
                                    {formatNumber(
                                      calculateTotalPrice(item.orderProducts) -
                                        item.paid
                                    )}
                                  </TableCell>
                                  <TableCell>
                                    {new Date(item.createdAt).toLocaleString()}
                                  </TableCell>
                                  <TableCell>{item.status}</TableCell>
                                  <TableCell
                                    style={isPrinting ? noPrintStyle : {}}
                                  >
                                    <Controls.ActionButton
                                      color="primary"
                                      title="View Detail"
                                      onClick={() => {
                                        history.push(`/print/${item.id}`);
                                      }}
                                    >
                                      <FiEye fontSize="medium" />
                                    </Controls.ActionButton>
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>

                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "flex-end",
                            flexDirection: "column",
                            p: 1,
                            gap: 0.5,
                            m: 1,
                            bgcolor: "background.paper",
                            borderRadius: 1,
                          }}
                        >
                          <Box display="flex" gap={1}>
                            <Typography
                              variant="body2"
                              fontWeight="bold"
                              whiteSpace="nowrap"
                            >
                              Total Price:
                            </Typography>
                            <Typography variant="body2">
                              {formatNumber(
                                calculateTotalSumOrderProducts(
                                  groupedOrders[customerId]
                                )
                              )}
                            </Typography>
                          </Box>

                          <Box display="flex" gap={1}>
                            <Typography
                              variant="body2"
                              fontWeight="bold"
                              whiteSpace="nowrap"
                            >
                              Total Paid:
                            </Typography>
                            <Typography variant="body2">
                              {formatNumber(
                                calculateTotalPaid(groupedOrders[customerId])
                              )}
                            </Typography>
                          </Box>

                          <Box display="flex" gap={1}>
                            <Typography
                              variant="body2"
                              fontWeight="bold"
                              whiteSpace="nowrap"
                            >
                              Total Balance:
                            </Typography>
                            <Typography variant="body2">
                              {formatNumber(
                                calculateTotalSumOrderProducts(
                                  groupedOrders[customerId]
                                ) -
                                  calculateTotalPaid(groupedOrders[customerId])
                              )}
                            </Typography>
                          </Box>
                        </Box>
                      </Paper>
                    </div>
                  ))
                )}

                {Object.keys(groupedOrders).length > 0 && (
                  <Paper
                    style={{
                      overflowX: "auto",
                      marginTop: "30px",
                      padding: "20px",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "flex-end",
                        flexDirection: "column",
                        gap: 1,
                        bgcolor: "background.paper",
                        borderRadius: 1,
                        p: 2,
                      }}
                    >
                      <Typography
                        variant="body2"
                        fontWeight="bold"
                        whiteSpace="nowrap"
                        gutterBottom
                      >
                        Total Summary (All Customers)
                      </Typography>

                      <Box display="flex" gap={1}>
                        <Typography
                          variant="body2"
                          fontWeight="bold"
                          whiteSpace="nowrap"
                        >
                          Total Price:
                        </Typography>
                        <Typography variant="body2">
                          {formatNumber(
                            calculateGrandTotalPrice(filteredOrders)
                          )}
                        </Typography>
                      </Box>

                      <Box display="flex" gap={1}>
                        <Typography
                          variant="body2"
                          fontWeight="bold"
                          whiteSpace="nowrap"
                        >
                          Total Paid:
                        </Typography>
                        <Typography variant="body2">
                          {formatNumber(
                            calculateGrandTotalPaid(filteredOrders)
                          )}
                        </Typography>
                      </Box>

                      <Box display="flex" gap={1}>
                        <Typography
                          variant="body2"
                          fontWeight="bold"
                          whiteSpace="nowrap"
                        >
                          Total Balance:
                        </Typography>
                        <Typography variant="body2">
                          {formatNumber(
                            calculateGrandTotalPrice(filteredOrders) -
                              calculateGrandTotalPaid(filteredOrders)
                          )}
                        </Typography>
                      </Box>
                    </Box>
                  </Paper>
                )}
                <div ref={bottomRef}></div>
              </Grid>
            </Grid>
          </div>
          <Notification notify={notify} setNotify={setNotify} />
          <div
            className={classes.scrollButton}
            onClick={handleScrollClick}
            title={getButtonLabel()}
          >
            <ArrowForwardIosIcon
              style={{ transform: `rotate(${getIconRotation()})` }}
            />
          </div>
        </div>
      </Admin>
    </Layout>
  );
};

export default PaymentReport;
